import { ReactNode } from "react";
import { useAppSelector } from "../../app/hooks";
import {
  getCompanyData,
  getMerchant,
  getPartnership,
} from "../../utils/companies";
import { ToastMessage } from "@allaria-tech/matter/ToastMessage";
import { ThemeProvider } from "@allaria-tech/matter/ThemeProvider";
import { Header } from "@allaria-tech/matter/Header";
import { Logo } from "@allaria-tech/matter/Logo";
import Stepper from "../Stepper/Stepper";
import SupportButton from "../SupportButton/SupportButton";
import useTheme from "../../hooks/useTheme";

import styles from "./Layout.module.scss";
import useWindowWidth from "../../hooks/useWindowWidth";
import useKeyboardInset from "../../hooks/useKeyboardInset";

interface Props {
  children: ReactNode;
  hideStepper?: boolean;
  hideHeader?: boolean;
  hideMobileHeader?: boolean;
  hideLogo?: boolean;
  centeredLogo?: boolean;
  hideSupportButton?: boolean;
  layoutClassName?: string;
  className?: string;
  isLoading?: boolean;
  dataId?: string;
  testId?: string;
}
const Layout = ({
  children,
  layoutClassName,
  className,
  hideStepper,
  hideHeader,
  hideMobileHeader,
  hideLogo,
  centeredLogo,
  hideSupportButton,
  isLoading,
  dataId,
  testId = "layout",
}: Props) => {
  const {
    container,
    matterHeader,
    logoBar,
    partnerLogo,
    companyLogo,
    headerSupportButton,
    contentRow,
    stepperColumn,
    body,
    bodyLoading,
    button,
  } = styles;

  const { formValues } = useAppSelector((state) => state.onboardingData);

  const { logo, logoKey, formTitle } = getCompanyData();
  const partnership = getPartnership(formValues);
  const merchant = getMerchant(formValues);

  const theme = useTheme();

  useKeyboardInset();

  const isMobile = useWindowWidth().width < 768;

  // A partnership flagged `replacesCompanyLogo` stands in for the brand logo
  // instead of sitting beside it, so ours is dropped (Header renders no logo
  // when `logoProduct` is omitted). `hideLogo` drops every logo: the crypto
  // onboarding renders as a bare modal inside the Allaria app.
  const showCompanyLogo = !partnership?.replacesCompanyLogo && !hideLogo;

  // Brands without a Matter lockup (e.g. Securities) have no `logoKey`, so the
  // mobile logo bar renders their own asset instead of letting `Logo` fall
  // back to its default product.
  const customCompanyLogo =
    showCompanyLogo && !logoKey ? (
      <img className={companyLogo} src={logo} alt={`${formTitle} logo`} />
    ) : undefined;

  // White-label partner/merchant branding renders beside the brand logo via
  // the Header's `children` slot (pulled left by `matterHeader`).
  const partnerLogos =
    !hideLogo && (partnership || merchant?.logo) ? (
      <>
        {partnership && (
          <img
            className={`${partnerLogo} ${styles[partnership.logoClassname] ?? ""}`}
            src={partnership.logo}
            alt={`${partnership.name} logo`}
          />
        )}
        {merchant?.logo && (
          <img
            className={partnerLogo}
            src={merchant.logo}
            alt={`${merchant.key} logo`}
          />
        )}
      </>
    ) : undefined;

  const headerSupport = hideSupportButton ? undefined : (
    <SupportButton
      variant="inline"
      className={headerSupportButton}
      testId={`${testId}-header-support`}
    />
  );

  const headerChildren =
    partnerLogos || headerSupport ? (
      <>
        {partnerLogos}
        {headerSupport}
      </>
    ) : undefined;

  const showHeaderBar = !hideHeader && !centeredLogo && !isMobile;

  const content = (
    <div
      className={`${container} ${layoutClassName ?? ""}`}
      data-id={dataId}
      data-testid={testId}
    >
      {showHeaderBar && (
        <Header
          className={matterHeader}
          logoProduct={showCompanyLogo ? logoKey : undefined}
        >
          {headerChildren}
        </Header>
      )}
      {!hideMobileHeader &&
        (centeredLogo || isMobile) &&
        (showCompanyLogo || partnerLogos) && (
          <div className={logoBar} data-testid={`${testId}-logo-bar`}>
            {showCompanyLogo && logoKey && <Logo product={logoKey} />}
            {customCompanyLogo}
            {partnerLogos}
          </div>
        )}
      <ToastMessage
        messageId="layoutPage"
        position="TOP"
        className="toastMessage"
      />
      <div className={contentRow} data-testid={`${testId}-content`}>
        {!isLoading && !hideStepper && (
          <div className={stepperColumn}>
            <Stepper testId={`${testId}-stepper`} />
          </div>
        )}
        <div
          className={`${isLoading ? bodyLoading : body} ${className ?? ""}`}
          data-testid={`${testId}-body`}
        >
          {children}
        </div>
      </div>
      {!showHeaderBar && !hideSupportButton && (
        <SupportButton className={button} testId={`${testId}-support-button`} />
      )}
    </div>
  );

  // While the active template's theme is still loading, render without the
  // ThemeProvider (the global CSS defaults keep the loader styled).
  return theme ? (
    <ThemeProvider theme={theme}>{content}</ThemeProvider>
  ) : (
    content
  );
};

export default Layout;
